// T3 (V218) — lógica pura del REPARTO GUIADO (sin React, para probarla con vitest).
// El tenant cuenta físicamente cada talla de cada sucursal; el backend convierte los
// conteos en movimientos del kardex (sale el pool sin talla, cada talla queda en su conteo).

export interface RepartoSize { variantId: string; name: string; isActive: boolean; onHand: number; negative: boolean }
/** Decision 5 — venta SIN talla (renglon viejo) cobrada en una sucursal ya repartida: se asigna a su talla. */
export interface RepartoPendiente { movementId: string; orderId: string | null; qty: number; createdAt: string }
export interface RepartoBranch {
  branchId: string; branchName: string; mode: 'pool' | 'desglosado'; pool: number; sizes: RepartoSize[];
  pendientes?: RepartoPendiente[];
}
export interface RepartoView { productId: string; productName: string; trackInventory: boolean; branches: RepartoBranch[] }
export interface NewVariantDraft { name: string; price: number; cost?: number }

/** Clave de captura: talla existente por id, talla nueva por `new:<i>`. */
export const sizeKey = (s: { variantId?: string; newIndex?: number }) =>
  s.variantId ? s.variantId : `new:${s.newIndex}`;

/** Una sucursal "tiene stock" si su pool o alguna talla es distinto de 0. */
export const branchHasStock = (b: RepartoBranch) => b.pool !== 0 || b.sizes.some((s) => s.onHand !== 0);

/** Prellenado: en un desglosado, el saldo actual (negativas en blanco: hay que contarlas);
 *  en pool, todo en blanco (el pool no dice cuánto hay de cada talla). */
export function initialCounts(b: RepartoBranch, newCount: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of b.sizes) out[s.variantId] = b.mode === 'desglosado' && s.onHand >= 0 ? String(s.onHand) : '';
  for (let i = 0; i < newCount; i++) out[`new:${i}`] = '';
  return out;
}

/** Conteo válido = entero >= 0 capturado. */
export const validCount = (v: string | undefined) => v != null && v.trim() !== '' && /^\d+$/.test(v.trim());

export interface BranchSummary { before: number; after: number | null; diff: number | null; missing: number }

export function summarize(b: RepartoBranch, counts: Record<string, string>, newCount: number): BranchSummary {
  const before = b.pool + b.sizes.reduce((a, s) => a + s.onHand, 0);
  const keys = [...b.sizes.map((s) => s.variantId), ...Array.from({ length: newCount }, (_, i) => `new:${i}`)];
  const missing = keys.filter((k) => !validCount(counts[k])).length;
  if (missing > 0) return { before, after: null, diff: null, missing };
  const after = keys.reduce((a, k) => a + Number(counts[k]), 0);
  return { before, after, diff: after - before, missing: 0 };
}

/** Cuerpo del POST /boutique/products/:id/reparto. */
export function buildPayload(
  repartoKey: string, view: RepartoView, selected: string[], countsByBranch: Record<string, Record<string, string>>,
  newVariants: NewVariantDraft[],
) {
  return {
    repartoKey,
    ...(newVariants.length ? { newVariants } : {}),
    branches: view.branches.filter((b) => selected.includes(b.branchId)).map((b) => {
      const counts = countsByBranch[b.branchId] ?? {};
      return {
        branchId: b.branchId,
        counts: [
          ...b.sizes.map((s) => ({ variantId: s.variantId, qty: Number(counts[s.variantId]) })),
          ...newVariants.map((_, i) => ({ newIndex: i, qty: Number(counts[`new:${i}`]) })),
        ],
      };
    }),
  };
}
